export interface VoiceTurn {
  role: "user" | "assistant";
  text: string;
}

export interface VoiceSessionScore {
  fluency_score: number;
  vocabulary_score: number;
  confidence_score: number;
  overall_score: number;
  message_count: number;
}

function clamp(value: number, min = 0, max = 100): number {
  return Math.max(min, Math.min(max, Math.round(value)));
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/\[final\]/g, "")
    .split(/[^\p{L}\p{N}']+/u)
    .filter(Boolean);
}

export function scoreVoiceSession(
  turns: VoiceTurn[],
  vocabulary: Array<{ word: string }> | null | undefined,
  durationSeconds: number,
): VoiceSessionScore {
  const cleaned = turns.map((turn) => ({
    ...turn,
    text: turn.text.replace(/\[final\]/g, "").trim(),
  })).filter((turn) => turn.text);

  const userTurns = cleaned.filter((turn) => turn.role === "user");
  const userWords = tokenize(userTurns.map((turn) => turn.text).join(" "));
  const avgUserWords = userTurns.length ? userWords.length / userTurns.length : 0;

  const fluency = userTurns.length === 0
    ? 0
    : clamp(25 + avgUserWords * 8, 15, 95);

  let vocabularyScore = 0;
  if (userTurns.length > 0) {
    const vocab = (vocabulary || []).map((item) => item.word.toLowerCase()).filter(Boolean);
    if (vocab.length) {
      const hits = vocab.filter((word) => userWords.some((token) => token.includes(word) || word.includes(token))).length;
      vocabularyScore = clamp(20 + (hits / Math.min(vocab.length, 10)) * 80, 20, 95);
    } else {
      vocabularyScore = clamp(30 + userWords.length * 2, 20, 80);
    }
  }

  const confidence = userTurns.length === 0
    ? 0
    : clamp(20 + userTurns.length * 10 + Math.min(durationSeconds / 8, 25), 20, 95);

  const overall = cleaned.length === 0
    ? 0
    : clamp((fluency + vocabularyScore + confidence) / 3);

  return {
    fluency_score: fluency,
    vocabulary_score: vocabularyScore,
    confidence_score: confidence,
    overall_score: overall,
    message_count: cleaned.length,
  };
}
